/* eslint-disable import/no-cycle */
import React, {
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  CustomFormProps,
  DefaultFormActions,
  Form,
  useEnhancedForm,
} from '../apiForm/form';
import { flattenUISchema } from './helper';
import {
  UISchema,
  UISchemaLanguage,
  UISchemaScope,
  FunctionMapping,
} from './types';
import mappings from './mapping';
import { CustomFormRef } from '../apiForm/form/CustomFormProps';
import { useForceSubmitDialog } from './useForceSubmitDialog';
import useDynaFormFactory from './useDynaFormFactory';
import SubmitContext from './FormContext';
import useResetOnSubmit from './useResetOnSubmit';

const DEFAULT_VALUES = {};

export const mapping: FunctionMapping = mappings;

export interface DynaFormProps extends CustomFormProps {
  uiSchema: UISchema;
  currentScope: UISchemaScope;
  isPreview?: boolean;
}

const DynaForm = forwardRef<CustomFormRef, DynaFormProps>(
  (
    {
      defaultValues = DEFAULT_VALUES,
      errors = {},
      onSubmit,
      uiSchema,
      currentScope = UISchemaScope.PROJECT,
      isPreview = false,
    },
    ref,
  ) => {
    const { i18n } = useTranslation();
    const language = useMemo<UISchemaLanguage>(
      () => (i18n.resolvedLanguage || 'de') as UISchemaLanguage,
      [i18n.resolvedLanguage],
    );

    const { handleSubmit, control, formState, reset, getValues } =
      useEnhancedForm({ defaultValues, errors });

    const { onSubmitWithReset } = useResetOnSubmit({
      onSubmit,
      getValues,
      reset,
    });

    const { toElements } = useDynaFormFactory();

    const [dynaFormRoot, setDynaFormRoot] = useState<ReactNode[]>([]);

    const { ConfirmationDialog, handleForceSubmit } = useForceSubmitDialog({
      formState,
      ref,
    });

    const forceSubmit = useCallback(async () => {
      const canChange = await handleForceSubmit();
      if (!canChange) return false;
      reset();
      return true;
    }, [handleForceSubmit, reset]);

    useEffect(() => {
      if (!uiSchema) return;
      setDynaFormRoot(
        toElements(
          {
            elementDescriptions: flattenUISchema(
              (uiSchema as UISchema).properties,
            ),
            mapping,
          },
          {
            control,
            language,
            currentScope,
            namePrefix: '',
          },
        ),
      );
    }, [control, currentScope, language, toElements, uiSchema]);

    return (
      <Form onSubmit={handleSubmit(onSubmitWithReset)}>
        <SubmitContext.Provider value={forceSubmit}>
          {dynaFormRoot}
        </SubmitContext.Provider>

        {!isPreview && (
          <>
            <ConfirmationDialog />
            <DefaultFormActions formState={formState} />
          </>
        )}
      </Form>
    );
  },
);

export default DynaForm;
